import { handlePostAnalytics, analyticEvents } from "@/apiUtils/analyticsUtils";
import { getCountryCodeFromLocale, convertTimeString, getChannelId } from "@/apiUtils/commonUtils";
import {
  addressCountryMappings,
  analyticEventTypes,
  EyeTestEventActions,
  EyeTestErrorCodes,
} from "@/apiUtils/constants";
import { updateAppointment, bookAppointment } from "@/apiUtils/storeUtils";
import { getService } from "@/api/mReserve/bookingApi";
import LoaderPanel from "@/common/components/LoaderPanel";
import {
  EyeTestServiceType,
  StoreType,
  MyInfoSectionContent,
  TestTypeContent,
  BookingDetails,
  ClientData,
  EyeTestMyInfoInputs,
  TypeBookingRequest,
} from "@/common/lib/types";
import { useGlobalStore } from "@/store/store";
import { useSearchParams, useParams, useRouter } from "next/navigation";
import { FC, useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import EyeTestUserInfoView from "./view";
import { MReserveService } from "@/api/mReserve/types";
import { getUserDetails } from "@/api/saleor/services";
import { defaultLocale } from "@/runtimeConfig/public";

interface EyeTestMyInfoProps {
  serviceType?: EyeTestServiceType;
  storeDetails?: StoreType;
  content?: MyInfoSectionContent;
  bookingDetails?: BookingDetails;
  serviceId?: number; // New: serviceId for mReserve API
}

const EyeTestMyInfo: FC<EyeTestMyInfoProps> = ({
  serviceType,
  storeDetails,
  content,
  bookingDetails,
  serviceId: propServiceId,
}) => {
  const searchParams = useSearchParams();
  const date = useMemo(() => searchParams.get("date") ?? undefined, [searchParams]);
  const slotStart = useMemo(() => searchParams.get("slotStart") ?? undefined, [searchParams]);
  const slotEnd = useMemo(() => searchParams.get("slotEnd") ?? undefined, [searchParams]);
  const storeId = useMemo(() => searchParams.get("store") ?? undefined, [searchParams]);

  // Extract serviceId from URL params if not passed as prop
  const serviceIdFromUrl = useMemo(() => {
    const urlServiceId = searchParams.get("serviceId");
    return urlServiceId ? Number(urlServiceId) : undefined;
  }, [searchParams]);

  // Use prop serviceId or URL serviceId
  const serviceId = propServiceId || serviceIdFromUrl;

  const [error, setError] = useState<{ active: boolean; message?: string }>({ active: false });
 const [serviceDetails, setServiceDetails] = useState<MReserveService | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sfContactKey, setSfContactKey] = useState<string | null>(null);


  const router = useRouter();
  const { locale } = useParams();
  // Ensure locale is a string (handle array case)
  const localeString = Array.isArray(locale) ? locale[0] : locale || "en";
  const country = getCountryCodeFromLocale(localeString);

  const { user } = useGlobalStore();

  const magrabiId = user?.magrabi_id;
  const channel = getChannelId(defaultLocale);

    useEffect(() => {
    const fetchSFContactKey = async () => {
      try {
        const userDetails: any = await getUserDetails(channel);
        
        // Save SFcontactKey to state
        if (userDetails?.SFcontactKey) {
          setSfContactKey(userDetails.SFcontactKey);
        } else {
          console.log(" SFcontactKey not found in GraphQL response");
        }
      } catch (error) {
        console.error("EyeTestMyInfo - Error fetching user details:", error);
      }
    };

    if (user?.email) {
      fetchSFContactKey();
    }
  }, [user?.email, channel]);

  const countryAddressData = addressCountryMappings?.[country] || {};
  const clientData: ClientData = useMemo(() => {
    if (bookingDetails?.customer) {
      return {
        firstName: bookingDetails?.customer?.firstName,
        lastName: bookingDetails?.customer?.lastName,
        email: bookingDetails?.customer?.email,
        phone:
          bookingDetails?.customer?.phoneNumber?.slice(countryAddressData?.dialCode?.length) ?? "",
      };
    }

    return {
      firstName: user?.given_name ?? "",
      lastName: user?.family_name ?? "",
      email: user?.email ?? "",
      phone: user?.phone_number?.slice(countryAddressData?.dialCode?.length) ?? "",
    };
  }, [bookingDetails, countryAddressData?.dialCode, user]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isLoading: onSubmitIsLoading },
  } = useForm<EyeTestMyInfoInputs>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { ...clientData, comments: bookingDetails?.comments },
  });

  // Fetch service name when serviceId changes
  useEffect(() => {
    if (serviceId) {
      getService(serviceId, localeString)
        .then((service) => {
          setServiceDetails(service);
        })
        .catch((error) => {
          console.error("Error fetching service:", error);
          // Fallback to undefined, will use static title from testTypes
        });
    }
  }, [serviceId, localeString]);

  const onSubmit = async (data: EyeTestMyInfoInputs) => {
    setIsLoading(true);
    if (data.terms && storeId && serviceId && date && slotStart) {
      const clientUser: ClientData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: countryAddressData?.dialCode + data.phone,
      };

      const startDateTime = date + " " + slotStart + ":00";

      // Use serviceId for mReserve API
      const requestBody: TypeBookingRequest & { serviceId?: number } = {
        storeId,
        startDatetime: startDateTime,
        user: clientUser,
        comment: data?.comments,
        service: undefined, // Not used for mReserve
        serviceId: serviceId, // Add serviceId for mReserve
      };

      if (magrabiId) {
        requestBody.mgr_customer_id = magrabiId;
      }

      if (sfContactKey) {
        requestBody.sfcontactkey = sfContactKey;
      }


      let response;

      try {
        if (bookingDetails?.id) {
          response = await updateAppointment(bookingDetails.id, requestBody, localeString);
        } else {
          response = await bookAppointment(requestBody, localeString);
        }

        if (response?.error) {
          if (
            response.error.code === EyeTestErrorCodes.BOOKING_FAILED ||
            response.error.code === EyeTestErrorCodes.UPDATE_FAILED
          ) {
            setError({ active: true, message: response.error.message });
          } else {
            setError({ active: true, message: "Something went wrong" });
          }
          setTimeout(() => setError({ active: false }), 2500);
          setIsLoading(false);
          return;
        }

        if (response?.data?.success || response?.data?.id) {
          handlePostAnalytics({
            eventType: analyticEventTypes.CUSTOM,
            data: {
              target: analyticEvents.BOOK_EYE_TEST,
              bookEyeTestData: {
                eventAction: EyeTestEventActions.MY_INFO,
                eventLabel: "",
              },
            },
          });

          handlePostAnalytics({
            eventType: analyticEventTypes.CUSTOM,
            data: {
              target: analyticEvents.BOOK_EYE_TEST_SUCCESS,
              bookEyeTestData: {
                testType: serviceType,
                storeName: storeDetails?.name,
                dateTime: date + " - " + convertTimeString(slotStart),
              },
            },
          });

          const bookingId = response.data.id || response.data.success;
          // ADD: Add timestamp to force fresh fetch and avoid stale cached data
          const timestamp = Date.now();
          router.push(`/${localeString}/book-eyetest/success?store=${storeId}&id=${bookingId}&_refresh=${timestamp}`);
        }
      } catch (error) {
        console.error("Error booking appointment:", error);
        setError({ active: true, message: "Something went wrong" });
        setTimeout(() => setError({ active: false }), 2500);
        setIsLoading(false);
      }
    }
  };

  const phoneNumber = watch("phone");
  const comments = watch("comments");

  const phoneNumberFormat = useMemo(() => {
    return phoneNumber?.startsWith("0")
      ? countryAddressData.phoneNumberPattern.FORMAT_3_DIGIT
      : countryAddressData.phoneNumberPattern.FORMAT_2_DIGIT;
  }, [phoneNumber, countryAddressData?.phoneNumberPattern]);

  return (
    <>
      {isLoading ? <LoaderPanel /> : ""}
      <EyeTestUserInfoView
        onSubmitIsLoading={isLoading}
        control={control}
        locale={localeString}
        content={content}
        serviceDetails={serviceDetails}
        storeDetails={storeDetails}
        country={country}
        selectedDate={date}
        slotStart={slotStart}
        slotEnd={slotEnd}
        errors={errors}
        phoneNumberFormat={phoneNumberFormat}
        dialCode={countryAddressData?.dialCode}
        error={error}
        comments={comments}
        setError={setError}
        register={register}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />
    </>
  );
};

export default EyeTestMyInfo;
